/**
 * Location utility functions for Spirit Research Lab
 */

import { logger } from '@/lib/logger';

interface GeocodingResult {
    name: string;
    country: string;
    lat: number;
    lon: number;
    timezone?: string;
    display_name?: string;
}

/**
 * Reverse geocodes coordinates to get city name, country, and timezone
 * Uses BigDataCloud API (Free Tier)
 */
export const reverseGeocode = async (lat: number, lon: number): Promise<GeocodingResult | null> => {
    try {
        const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=en`
        );
        const data = await response.json();

        // Extract IANA timezone name (e.g., "America/New_York")
        const tzInfo = data.localityInfo?.informative?.find(
            (info: { description?: string; order?: number; name?: string }) =>
                info.description?.toLowerCase().includes('time zone') ||
                info.name?.includes('/')
        );

        return {
            name: data.city || data.locality || data.principalSubdivision || 'Unknown',
            country: data.countryName,
            lat,
            lon,
            timezone: tzInfo?.name,
            display_name: `${data.city || data.locality}, ${data.countryName}`
        };
    } catch (error) {
        logger.error('Failed to reverse geocode', error as Error, { lat, lon });
        return null;
    }
};

/**
 * Attempts to get the user's current location via Browser Geolocation API
 */
export const getCurrentLocation = (): Promise<{ lat: number; lon: number } | null> => {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve(null);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lon: position.coords.longitude
                });
            },
            (error) => {
                logger.warn('Geolocation error', { error });
                resolve(null);
            },
            { timeout: 5000 }
        );
    });
};
